import React from 'react';
import { useSelector } from 'react-redux';

import { Card, Statistic, Row, Col } from 'antd';

import {
  CASH_APPROACH,
  HISTORICAL_SCENARIO,
  LIMIT_METRIC_ALPHA,
  LIMIT_METRIC_BETA,
  LIMIT_METRIC_CHOICES,
  LIMIT_METRIC_SHARPE,
  LIMIT_METRIC_TREYNOR,
  LIMIT_METRIC_VAR,
  REAL_TIME_SCENARIO,
  RISK_WEIGHTED_APPROACH,
  selectors,
} from 'state';
import { formatters } from 'services';
import { useReportingCurrency } from '../../../../state/app/utils';
import { useMetricsData } from '../../useMetricsData';
import {
  activeSession,
  sessionHasRepos,
} from '../../../../state/app/selectors';

const OverviewCards: React.FC = () => {
  const {
    risk_weighted_limit,
    risk_approach,
    client_money,
    scenario_type,
    metrics_limits,
    display_metrics,
  } = useSelector(selectors.activeSession);
  const session_obj = useSelector(selectors.activeSession);
  const {
    // market_value,
    last_market_value,
    risk_weighted_value,
    // portfolio_return,
    last_portfolio_return,
    // pnl_sum,
    last_pnl_sum,
    metrics,
    // fx_return,
    last_fx_return,
    session,
    last_metrics,
  } = useSelector(selectors.player);

  const player = useSelector(selectors.player);
  const totalCurrentLoans = useSelector(selectors.totalCurrentLoans);
  const totalCurrentDepos = useSelector(selectors.totalCurrentDepos);
  const reportingCurrency = useReportingCurrency(session);
  const reportingWallet = useSelector(
    selectors.walletByCurrency(reportingCurrency)
  );
  const cbBorrowedAmount = reportingWallet?.cb_deposit_amount ?? 0;
  const { var05 } = metrics.slice(-1)[0];

  const instruments = useSelector(selectors.financialInstrumentsList);
  const ccys = useSelector(selectors.playableCurrencies(instruments));
  const differentCCYs = ccys.length > 1;

  const hasHTM = useSelector(selectors.sessionHasHTMBonds);
  const hasAFS = useSelector(selectors.sessionHasAFSBonds);
  const HTMorAFS = hasAFS || hasHTM;
  const hasRepos = useSelector(selectors.sessionHasRepos);

  const lastMetrics = { ...(metrics.slice(-1)[0] || {}) };
  delete lastMetrics.timestamp;
  delete lastMetrics.id;

  const last_metrics_data: {
    name: string;
    value: string;
    key: number;
    raw_value: number;
  }[] = [
    {
      key: 1000, // not assigned yet for limits
      name: 'Volatility',
      value: formatters.metricsPercent1Digit(lastMetrics.portfolio_volatility),
      raw_value: lastMetrics.portfolio_volatility,
    },
    {
      key: LIMIT_METRIC_ALPHA,
      name: 'Alpha',
      value: formatters.metricsCommas4Digits(lastMetrics.alpha),
      raw_value: lastMetrics.alpha,
    },
    {
      key: LIMIT_METRIC_BETA,
      name: 'Beta',
      value: formatters.metricsCommas4Digits(lastMetrics.beta),
      raw_value: lastMetrics.beta,
    },
    {
      key: LIMIT_METRIC_SHARPE,
      name: 'Sharpe Ratio',
      value: formatters.metricsCommas2Digits(lastMetrics.sharpe_ratio),
      raw_value: lastMetrics.sharpe_ratio,
    },
    {
      key: LIMIT_METRIC_TREYNOR,
      name: 'Treynor Ratio',
      value: formatters.metricsCommas2Digits(lastMetrics.treynor_ratio),
      raw_value: lastMetrics.treynor_ratio,
    },
  ];
  if (!HTMorAFS) {
    last_metrics_data.push({
      key: LIMIT_METRIC_VAR,
      name: 'VaR, ' + reportingCurrency.symbol,
      value: formatters.million(lastMetrics.var05),
      raw_value: lastMetrics.var05,
    });
  }
  const last_metrics_obj = last_metrics_data.find(
    item => item.key === session_obj.limit_metric
  );
  let statStyle = { color: 'black' };
  if (session_obj.limit_metric !== null && last_metrics_obj !== undefined) {
    if (
      last_metrics_obj.raw_value < session_obj.metric_limit_low ||
      last_metrics_obj.raw_value > session_obj.metric_limit_high
    ) {
      statStyle = { color: 'red' };
    }
  }
  const StatsCard = (title: string, value: string, is_limit_card = false) => {
    let statStyle = { color: 'black' };
    if (is_limit_card) {
      if (session_obj.limit_metric !== null && last_metrics_obj !== undefined) {
        if (
          last_metrics_obj.raw_value < session_obj.metric_limit_low ||
          last_metrics_obj.raw_value > session_obj.metric_limit_high
        ) {
          statStyle = { color: 'red' };
        }
      }
    }
    return (
      <Card
        style={{ whiteSpace: 'nowrap', width: 'auto', display: 'inline-block' }}
      >
        <Statistic title={title} value={value} valueStyle={statStyle} />
      </Card>
    );
  };
  const MarketValueCard = StatsCard(
    'Market Value, ' + reportingCurrency.symbol,
    formatters.million(last_market_value)
  );
  const ReturnCard = StatsCard(
    'Return %',
    formatters.percent2Digit(last_portfolio_return)
  );
  // let limitMetricTitle = '';
  // if (session_obj.limit_metric !== undefined) {
  //   limitMetricTitle = `${LIMIT_METRIC_CHOICES[session_obj.limit_metric]}`;
  // }
  const LimitMetricCard = StatsCard(
    last_metrics_obj?.name || '',
    last_metrics_obj?.value || '0',
    true
  );
  const PNLCard = StatsCard(
    'PnL, ' + reportingCurrency.symbol,
    formatters.million(last_pnl_sum)
  );
  const RiskWeightedValueCard = StatsCard(
    'Risk weighted value, ' + reportingCurrency.symbol,
    formatters.million(risk_weighted_value)
  );
  const RiskWeightedLimitCard = StatsCard(
    'Risk weighted limit, ' + reportingCurrency.symbol,
    formatters.million(risk_weighted_limit)
  );
  const VarCard = StatsCard(
    'VaR, ' + reportingCurrency.symbol,
    formatters.million(var05)
  );
  const FXReturnCard = StatsCard(
    'Cash Txn MTM %',
    formatters.percent2Digit(last_fx_return / session_obj.starting_cash_amount)
  );
  // if (
  //   scenario_type === HISTORICAL_SCENARIO &&
  //   risk_approach === CASH_APPROACH
  // ) {
  //   return (
  //     <>
  //       {MarketValueCard}
  //       {FXReturnCard}
  //       {ReturnCard}
  //     </>
  //   );
  // }
  const lastMetricsData = useMetricsData();

  if (client_money) {
    const cash =
      session_obj.starting_cash_amount -
      player.total_repoed_amount +
      totalCurrentDepos -
      totalCurrentLoans +
      player.interbank_amount -
      player.trading_book;

    // console.log(`starting_cash_amount: ${session_obj.starting_cash_amount}
    //             player.total_repoed_amount: ${player.total_repoed_amount}
    //             totalCurrentDepos: ${totalCurrentDepos}
    //             totalCurrentLoans: ${totalCurrentLoans}
    //             player.interbank_amount: ${player.interbank_amount}
    //             player.trading_book: ${player.trading_book}
    //
    //             total cash: ${cash}
    // `);
    return (
      <Card>
        <Row>
          <Col span={12}>
            <div>
              Capital: {formatters.million(session_obj.starting_cash_amount)}{' '}
              {/*{reportingCurrency.symbol}*/}
            </div>
            <div>
              Trading Book: {formatters.million(player.trading_book)}{' '}
              {/*{reportingCurrency.symbol}*/}
            </div>
            {/*<div>*/}
            {/*  Cash: {formatters.million(cash)}*/}
            {/*  /!*{reportingCurrency.symbol}*!/*/}
            {/*</div>*/}
            <div>
              Client Loans: {formatters.million(totalCurrentLoans)}{' '}
              {/*{reportingCurrency.symbol}*/}
            </div>
            <div>
              Client Depos: {formatters.million(totalCurrentDepos)}{' '}
              {/*{reportingCurrency.symbol}*/}
            </div>
            <div>
              Interbank Amount: {formatters.million(player.interbank_amount)}{' '}
              {/*{formatters.million(player.interbank_amount + cbBorrowedAmount)}{' '}*/}
              {/*{reportingCurrency.symbol}*/}
            </div>
            {hasRepos && (
              <div>
                Repos: {formatters.million(player.total_repoed_amount)}{' '}
                {/*{reportingCurrency.symbol}*/}
              </div>
            )}
          </Col>
          <Col span={12}>
            <div>
              Trading Book: {formatters.million(player.trading_book_pnl)}{' '}
              {/*{reportingCurrency.symbol}*/}
            </div>
            <div>
              Net client interest: {formatters.million(player.client_interest)}{' '}
              {/*{reportingCurrency.symbol}*/}
            </div>
            <div>
              Net interbank interest:{' '}
              {formatters.million(player.interbank_interest)}{' '}
              {/*{reportingCurrency.symbol}*/}
            </div>
            <div>
              Emergency interest:
              {formatters.million(player.paid_lombard_interest)}{' '}
              {/*{reportingCurrency.symbol}*/}
            </div>
            {hasRepos && (
              <div>
                Repos interest: -{formatters.million(player.paid_repo_interest)}{' '}
                {/*{reportingCurrency.symbol}*/}
              </div>
            )}
          </Col>
        </Row>
      </Card>
    );
  }

  if (risk_approach === CASH_APPROACH)
    // return (
    //   <div>
    //     <Row>
    //       <Col span={14}>{MarketValueCard}</Col>
    //       {session_obj.limit_metric !== null && (
    //         <Col span={10}>{LimitMetricCard}</Col>
    //       )}
    //       {differentCCYs && <Col span={14}>{FXReturnCard}</Col>}
    //       <Col span={10}>{ReturnCard}</Col>
    //     </Row>
    //     {/*<Row>*/}
    //     {/*  <Col span={10}>{ReturnCard}</Col>*/}
    //     {/*  <Col span={14}>{AccruedInterestCard}</Col>*/}
    //     {/*</Row>*/}
    //   </div>
    // );
    return (
      <div>
        <Row style={{ flexFlow: 'row nowrap' }}>
          <Col>{MarketValueCard}</Col>
          {session_obj.limit_metric !== null && <Col>{LimitMetricCard}</Col>}
          {differentCCYs && <Col>{FXReturnCard}</Col>}
          <Col>{ReturnCard}</Col>
        </Row>
        <Row style={{ flexFlow: 'row nowrap' }}>
          {lastMetricsData.map(item => {
            const selectedMetricLimits = metrics_limits.find(
              metric => metric.metric_type === item.key
            );
            let style = { color: 'black' };

            if (
              selectedMetricLimits !== undefined &&
              selectedMetricLimits.enforced
            ) {
              const hasOnlyUpperLimit =
                selectedMetricLimits.upper_limit !== null &&
                selectedMetricLimits.lower_limit === null;
              const hasOnlyLowerLimit =
                selectedMetricLimits.upper_limit === null &&
                selectedMetricLimits.lower_limit !== null;
              if (hasOnlyUpperLimit) {
                if (item.raw_value > selectedMetricLimits.upper_limit) {
                  style = { color: 'red' };
                }
              } else if (hasOnlyLowerLimit) {
                if (item.raw_value < selectedMetricLimits.lower_limit) {
                  style = { color: 'red' };
                }
              } else {
                // hasBoth
                if (
                  item.raw_value < selectedMetricLimits.lower_limit ||
                  item.raw_value > selectedMetricLimits.upper_limit
                ) {
                  style = { color: 'red' };
                }
              }

              return (
                <Col key={item.name}>
                  <Card>
                    <Statistic
                      title={
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {item.name}
                        </span>
                      }
                      value={item.value}
                      valueStyle={{ ...style, whiteSpace: 'nowrap' }}
                      style={{ height: '58px' }}
                    />
                  </Card>
                </Col>
              );
            }
          })}
        </Row>
        {/*<Row>*/}
        {/*  <Col span={10}>{ReturnCard}</Col>*/}
        {/*  <Col span={14}>{AccruedInterestCard}</Col>*/}
        {/*</Row>*/}
      </div>
    );

  if (risk_approach === RISK_WEIGHTED_APPROACH)
    return (
      <div>
        <Row>
          <Col span={8}>{MarketValueCard}</Col>
          <Col span={8}>{ReturnCard}</Col>
          <Col span={8}>{PNLCard}</Col>
        </Row>
        <Row>
          <Col span={8}>{RiskWeightedValueCard}</Col>
          <Col span={8}>{RiskWeightedLimitCard}</Col>
          <Col span={8}>{VarCard}</Col>
        </Row>
      </div>
    );

  return null;
};

export default React.memo(OverviewCards);
